import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, HandshakeIcon, BadgeCheck, Sparkles, ClipboardCheck, Search, Send } from "lucide-react";
import { useText } from "../context/ContentContext";

const PROCESS_STEPS = [
    {
        n: "01",
        title: "In-depth intake",
        body: "Every client completes a guided questionnaire and a short video call with Thom. We build a full client profile — goals, timeline, communication style — before any agent is contacted.",
        image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1400&q=80",
    },
    {
        n: "02",
        title: "Board research",
        body: "We independently research agents in the client's target market using local board data, production history, and direct conversations. No paid placements, no bidding lists.",
        image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1400&q=80",
    },
    {
        n: "03",
        title: "Selection",
        body: "If your track record, communication style and specialization match the client's needs, you're shortlisted. Typically two or three agents are introduced per client.",
        image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=80",
    },
    {
        n: "04",
        title: "Warm introduction",
        body: "A formal OREA Referral Agreement is signed, then we make a warm introduction so you can move quickly. The client is already expecting your call.",
        image: "https://images.pexels.com/photos/7578903/pexels-photo-7578903.jpeg?auto=compress&cs=tinysrgb&w=1400",
    },
];

const BENEFITS = [
    {
        icon: Users,
        title: "Motivated clients",
        body: "Every client is pre-screened on a video call and already committed to the matching process before you ever hear about them.",
    },
    {
        icon: HandshakeIcon,
        title: "Smoother deals",
        body: "Personality and communication-style fit cuts friction throughout the transaction — fewer surprises, fewer last-minute walks.",
    },
    {
        icon: BadgeCheck,
        title: "No upfront cost",
        body: "Pay a 25% referral fee from your side of the commission only when the transaction successfully closes. No subscriptions, no lead fees.",
    },
    {
        icon: Sparkles,
        title: "Quality over volume",
        body: "We deliberately keep the network small per area. You won't compete against five other agents for the same client.",
    },
];

const ROLE_POINTS = [
    {
        icon: ClipboardCheck,
        text: "Respond promptly to introductions and schedule the initial consultation within 48 hours.",
    },
    {
        icon: Search,
        text: "Deliver your normal high standard of service, with periodic status updates back to RealtorReferral.ca.",
    },
    {
        icon: Send,
        text: "Ensure the referral commission is paid through iCloud Realty Ltd on closing, as per the signed OREA Referral Agreement.",
    },
];

export default function AgentsPage() {
    const eyebrow = useText("agents_eyebrow", "For Agents");
    const part1 = useText("agents_title_part1", "High-intent clients,");
    const emphasis = useText("agents_title_emphasis", "personally matched");
    const part2 = useText("agents_title_part2", "to you.");
    const subtitle = useText(
        "agents_subtitle",
        "Personality-based referrals to buyers and sellers in your trade area."
    );

    const processEyebrow = useText("agents_process_eyebrow", "The Agent Process");
    const processTitle = useText("agents_process_title", "How a referral reaches you");
    const processSubtitle = useText(
        "agents_process_subtitle",
        "Every introduction starts with a real conversation with the client."
    );
    const benefitsTitle = useText("agents_benefits_title", "Why join RealtorReferral.ca");
    const roleTitle = useText("agents_role_title", "Your Role");
    const roleIntro = useText("agents_role_intro", "We do the matching. You do what you already do best.");
    const feeNote = useText(
        "agents_fee_note",
        "Referral commissions are documented using the OREA Referral Agreement."
    );

    const [activeStep, setActiveStep] = useState(0);

    return (
        <div data-testid="agents-page">
            {/* Hero */}
            <section className="bg-brand-paper paper-grain">
                <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-20 lg:pt-28 pb-20 lg:pb-28">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="grid lg:grid-cols-12 gap-12 items-center"
                    >
                        <div className="lg:col-span-7">
                            <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-6">
                                — {eyebrow}
                            </div>
                            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-brand-ink">
                                {part1}{" "}
                                <span className="italic font-normal">{emphasis}</span>{" "}
                                {part2}
                            </h1>
                            <p className="mt-8 text-lg lg:text-xl text-brand-muted leading-relaxed max-w-xl">
                                {subtitle}
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <a
                                    href="#agent-process"
                                    data-testid="agents-hero-cta"
                                    className="inline-flex items-center gap-3 bg-brand-ink text-brand-paper px-8 py-4 text-sm tracking-wide hover:bg-black transition-all hover:gap-4"
                                >
                                    See how it works
                                    <span aria-hidden>→</span>
                                </a>
                            </div>

                            <div className="mt-16 flex flex-wrap gap-8 text-sm text-brand-muted">
                                <div>
                                    <div className="font-serif text-3xl text-brand-ink">25%</div>
                                    <div className="text-xs uppercase tracking-wider mt-1">Referral fee, on closing</div>
                                </div>
                                <div>
                                    <div className="font-serif text-3xl text-brand-ink">Warm</div>
                                    <div className="text-xs uppercase tracking-wider mt-1">Every intro is briefed</div>
                                </div>
                                <div>
                                    <div className="font-serif text-3xl text-brand-ink">OREA</div>
                                    <div className="text-xs uppercase tracking-wider mt-1">Documented agreement</div>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-5 image-zoom h-[460px] lg:h-[600px]">
                            <img
                                src="https://images.pexels.com/photos/8815909/pexels-photo-8815909.jpeg?auto=compress&cs=tinysrgb&w=1400"
                                alt="Agents shaking hands"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Process */}
            <section id="agent-process" className="bg-brand-sand">
                <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
                    <div className="grid lg:grid-cols-12 gap-8 mb-16">
                        <div className="lg:col-span-5">
                            <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                                — {processEyebrow}
                            </div>
                            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-brand-ink">
                                {processTitle}
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                            <p className="text-lg text-brand-muted leading-relaxed">{processSubtitle}</p>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-12 items-start">
                        <div className="lg:col-span-6 space-y-2">
                            {PROCESS_STEPS.map((step, i) => {
                                const isActive = activeStep === i;
                                return (
                                    <motion.button
                                        key={step.n}
                                        onMouseEnter={() => setActiveStep(i)}
                                        onFocus={() => setActiveStep(i)}
                                        onClick={() => setActiveStep(i)}
                                        data-testid={`agent-step-${i}`}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-80px" }}
                                        transition={{ duration: 0.6, delay: i * 0.1 }}
                                        className={`block w-full text-left border-t border-brand-line py-7 transition-colors ${
                                            isActive ? "bg-brand-paper/50" : ""
                                        } ${i === PROCESS_STEPS.length - 1 ? "border-b" : ""}`}
                                    >
                                        <div className="flex items-start gap-6 px-2">
                                            <div className="font-serif text-3xl text-brand-gold w-14 shrink-0">
                                                {step.n}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-serif text-2xl lg:text-3xl text-brand-ink leading-tight">
                                                    {step.title}
                                                </h3>
                                                <p
                                                    className={`text-brand-muted mt-2 text-base leading-relaxed transition-all overflow-hidden ${
                                                        isActive ? "max-h-40 opacity-100" : "lg:max-h-0 lg:opacity-0"
                                                    }`}
                                                >
                                                    {step.body}
                                                </p>
                                                <p className="lg:hidden text-brand-muted mt-2 text-base leading-relaxed">
                                                    {step.body}
                                                </p>
                                            </div>
                                            <div
                                                className={`text-brand-ink text-xl transition-transform ${
                                                    isActive ? "rotate-45" : ""
                                                }`}
                                            >
                                                +
                                            </div>
                                        </div>
                                    </motion.button>
                                );
                            })}
                        </div>

                        <div className="lg:col-span-6 sticky top-28">
                            <div className="relative image-zoom h-[480px] lg:h-[600px]">
                                {PROCESS_STEPS.map((step, i) => (
                                    <motion.img
                                        key={step.n}
                                        src={step.image}
                                        alt={step.title}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: activeStep === i ? 1 : 0 }}
                                        transition={{ duration: 0.7 }}
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                ))}
                                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-brand-paper">
                                    <div className="text-xs uppercase tracking-[0.25em] bg-brand-ink/80 px-3 py-2 backdrop-blur">
                                        Step {PROCESS_STEPS[activeStep].n}
                                    </div>
                                    <div className="font-serif text-2xl bg-brand-ink/80 px-4 py-2 backdrop-blur">
                                        {PROCESS_STEPS[activeStep].title}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="bg-brand-paper">
                <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
                    <div className="max-w-2xl mb-16">
                        <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                            — The benefits
                        </div>
                        <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-brand-ink">
                            {benefitsTitle}
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-line border border-brand-line">
                        {BENEFITS.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <motion.div
                                    key={b.title}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{ duration: 0.6, delay: i * 0.1 }}
                                    className="bg-brand-paper p-10 group hover:bg-brand-sand/50 transition-colors"
                                    data-testid={`agent-benefit-${i}`}
                                >
                                    <Icon size={28} strokeWidth={1.25} className="text-brand-gold mb-8 transition-transform group-hover:-translate-y-1" />
                                    <h3 className="font-serif text-2xl text-brand-ink mb-3 leading-tight">
                                        {b.title}
                                    </h3>
                                    <p className="text-brand-muted leading-relaxed text-sm">{b.body}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Your Role */}
            <section className="bg-brand-sand border-y border-brand-line">
                <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                                — A simple deal
                            </div>
                            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-brand-ink mb-6">
                                {roleTitle}
                            </h2>
                            <p className="text-brand-muted text-lg leading-relaxed">{roleIntro}</p>
                        </div>
                        <div className="lg:col-span-7 lg:col-start-6 space-y-6">
                            {ROLE_POINTS.map((point, i) => {
                                const Icon = point.icon;
                                return (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-80px" }}
                                        transition={{ duration: 0.5, delay: i * 0.12 }}
                                        className="flex items-start gap-5 border-t border-brand-line pt-6"
                                    >
                                        <Icon size={22} strokeWidth={1.25} className="text-brand-gold mt-1 shrink-0" />
                                        <p className="text-lg text-brand-ink/85 leading-relaxed">{point.text}</p>
                                    </motion.div>
                                );
                            })}
                            <p className="text-sm text-brand-muted pt-6 border-t border-brand-line italic">
                                {feeNote}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Closing CTA — back to the client side */}
            <section className="bg-brand-paper">
                <div className="mx-auto max-w-4xl px-6 lg:px-10 py-24 text-center">
                    <h2 className="font-serif text-3xl lg:text-4xl text-brand-ink leading-tight mb-6">
                        Looking for a realtor instead?
                    </h2>
                    <p className="text-brand-muted text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                        If you're buying or selling and want to be personally matched with the right
                        agent, start a short intake — we'll take it from there.
                    </p>
                    <Link
                        to="/get-started"
                        data-testid="agents-client-cta"
                        className="inline-flex items-center gap-3 bg-brand-ink text-brand-paper px-8 py-4 text-sm tracking-wide hover:bg-black transition-all hover:gap-4"
                    >
                        Get Matched Now!
                        <span aria-hidden>→</span>
                    </Link>
                </div>
            </section>
        </div>
    );
}
